import type {
  AppPageProps,
  RenderDocumentProps,
  RenderingStrategy,
  SlugPageProps,
} from '@/cms/rendering-strategy/types';
import type { PropsWithChildren } from 'react';
import * as React from 'react';
import { useEffect } from 'react';
import { HrefLangLinks } from '@/scopes/common/components/Scripts/HrefLangLinks';
import { ThirdPartyScripts } from '@/scopes/common/components/Scripts/ThirdPartyScripts';
import { brandingFont, generalSansFont } from '../../framework/react-styles';
import { SetABNewRelicCustomAttribute } from '@/scopes/common/components/Scripts/setABNewRelicCustomAttribute';
import NextHead from 'next/head';
import actions from '@/scopes/common/domain/use-cases/useCases';
import cookieClient from '@/scopes/common/infrastructure/cookies/cookieClient';
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient';
import { reactPageRegistry as defaultPageRegistry } from '@/react-pages';
import type { AllCMSPageFields } from '@/cms/fields/mappers/common';
import StyledComponentsRegistry from '@/framework/styled-components/styled-components-registry';

export function fullReactRenderingStrategy(): RenderingStrategy {
  function renderDocument({ Head, Html, Main, NextScript, pageProps }: RenderDocumentProps): React.JSX.Element {
    const page = pageProps?.page;

    const { name, template, fields } = page || {};
    const { bodyAttrs, htmlLang } = fields || {};
    const { machineName } = bodyAttrs || {};
    const { fields: pagePropsFields, config } = pageProps;
    const pageTitle = pagePropsFields?.pagetitle || config.brandName;
    const canonicalUrl = `${config.domainUrl}${pagePropsFields?._url}`;
    const baseAssetsImgUrl = `${config.domainUrl}/DesignSystem/WebAssets/${config.brandName}/img`;

    return (
      <Html lang={htmlLang}>
        <Head>
          {Boolean(pagePropsFields?.pagemetadescription) && (
            <meta name="description" content={pagePropsFields.pagemetadescription as string | undefined} />
          )}
          <meta
            name="robots"
            content={`${pagePropsFields?.metarobotsindex ? 'index' : 'noindex'},${
              pagePropsFields?.metarobotsfollow ? 'follow' : 'nofollow'
            }`}
          />
          <link rel="shortcut icon" type="image/x-icon" href={`${baseAssetsImgUrl}/favicon.ico`} />
          <link rel="apple-touch-icon" sizes="180x180" href={`${baseAssetsImgUrl}/apple-touch-icon.png`} />
          <link rel="icon" type="image/png" sizes="32x32" href={`${baseAssetsImgUrl}/favicon-32x32.png`} />
          <link rel="icon" type="image/png" sizes="16x16" href={`${baseAssetsImgUrl}/favicon-16x16.png`} />
          {Boolean(pagePropsFields?.hreflangautomatic) && (
            <HrefLangLinks links={pagePropsFields?.hreflangautomatic as string} />
          )}
          <meta httpEquiv="x-ua-compatible" content="ie=edge" />
          <link rel="canonical" href={canonicalUrl} />
          <ThirdPartyScripts configuration={config.thirdParties} />
          <script
            type="application/ld+json"
            //eslint-disable-next-line react/no-danger -- See useInitLegacyDesignSystemAfterRehydration hook
            dangerouslySetInnerHTML={{
              __html: `
                {
                  "@context": "https://schema.org",
                  "@type": "Organization",
                  "name": "${config.brandName}",
                  "url": "${config.domainUrl}",
                  "logo": "${config.domainUrl}/-/media/sundio/sunwebsplashpage/logo/sunweb-main-logo.ashx"
                }
            `,
            }}
          />
          <script
            type="application/ld+json"
            //eslint-disable-next-line react/no-danger -- See useInitLegacyDesignSystemAfterRehydration hook
            dangerouslySetInnerHTML={{
              __html: `
              {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "${pageTitle}",
                "url": "${canonicalUrl}"
              }
            `,
            }}
          />
          {/*
              TODO missing tags:
              - CanonicalTagLinkOverride for every page
              - Generic script
              - Missing data structure for SEO
            */}
        </Head>
        <body
          className={`${brandingFont.variable} ${generalSansFont.variable}`}
          data-qa-full-react="true"
          data-in-name={machineName}
          data-qa-page={name}
          data-qa-template={template}
        >
          <StyledComponentsRegistry>
            <Main />
            <NextScript />
          </StyledComponentsRegistry>
        </body>
      </Html>
    );
  }

  function renderApp({ Component, ReactPage, pageProps }: AppPageProps) {
    const locale = pageProps.config.locale;
    const pageTitle = pageProps?.fields?.pagetitle || pageProps.config.brandName;

    return (
      <FullReactPage>
        <ReactPage
          translations={pageProps.translations}
          locale={locale}
          // TODO: This casting should be removed. In order to do so we need to remove exceptions for pages not using Uniform data
          cmsFields={pageProps?.cmsFields as AllCMSPageFields}
          config={pageProps.config}
        >
          <SetABNewRelicCustomAttribute cookieEnabled={Boolean(pageProps?.enableAbTest)} />
          <NextHead>
            <title>{pageTitle}</title>
          </NextHead>
          <Component {...pageProps} />
        </ReactPage>
      </FullReactPage>
    );
  }

  function renderPageComponent({
    reactPageRegistry = defaultPageRegistry,
    ...pageProps
  }: SlugPageProps): React.JSX.Element {
    const PageComponent = reactPageRegistry.getComponentFor(pageProps);

    return <PageComponent {...pageProps} />;
  }

  return {
    type: 'fullReact',
    renderDocument,
    renderApp,
    renderPageComponent,
  };
}

const loadPageUseCases = actions({ analyticsClient: ga4analyticsClient, cookieClient });

function FullReactPage({ children }: PropsWithChildren) {
  useEffect(() => {
    loadPageUseCases.reactPageLoaded();
  }, []);

  return children;
}
